import { Component, ViewChild } from '@angular/core';
import { StorageService } from '../../core/storage/storageService';
import { ActivatedRoute, Router } from '@angular/router';
import { AccessManager } from '../../components/ui/access-manager/access-manager';
import { UIErrorHandler } from '../../core/uiErrorHandler';
import { InvoiceService } from '../../models/invoices/invoiceService';
import { Customer } from '../../models/customer/customer';
import { TextModes } from '../../components/ui/controls/text-input/text-input.component';
import { UIHelpers } from '../../core/ui-helpers';
import { ListItem } from '../../../../../../libs/Shared/components/model/listItem';
import { Invoice } from '../../models/invoices/invoice';
import { InvoiceItem } from '../../models/invoices/invoiceItem';
import { ErrorDialogService } from '../../core/error-dialog/error-dialog.service';
import { CustomSnackbarComponent } from '../../components/ui/snackbar/custom-snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InvoiceLineItemsComponent } from '../../components/invoices/invoice-line-items/invoice-line-items.component';
import { TerminalCustomerComponent } from '../terminal/terminal-customer/terminal-customer.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { InvoicePreviewComponent } from '../../components/invoices/invoice-preview/invoice-preview.component';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-create-invoice',
  templateUrl: './create-invoice.component.html',
  standalone: false,
})
export class CreateInvoiceComponent {
  @ViewChild('lineItemsComponent') lineItemsComponent:
    | InvoiceLineItemsComponent
    | undefined;
  @ViewChild('customerComponent') customerComponent:
    | TerminalCustomerComponent
    | undefined;

  invoiceId: string | undefined;
  merchantId: string | undefined;
  selectedCustomer: Partial<Customer> | undefined;
  subtotal: number = 0;
  total: number = 0;
  totalFormatted: string = '';
  tax: number = 0;
  taxFormatted: string = '';
  sendCustomerReceipt: boolean = false;
  sendMerchantReceipt: boolean = false;
  numberOfInstallments: number = 0;
  paymentInstallments: boolean = false;
  paymentInstallmentsAmount: number = 0;
  paymentInstallmentsAmountFormatted: string = '';
  dueByDate: Date = new Date();
  pauseRemindersEnabled: boolean = false;
  pauseRemindersUntil: Date = new Date();
  invoiceNotes: string = '';

  emailReminderFrequency: string = '';
  customReminderFrequency: number = 0;
  sendEmailReminders: boolean = false;

  invoice: Invoice = Invoice.createEmpty();
  invoiceItems: InvoiceItem[] = [];

  viewInvoiceMode: boolean = false;
  invoicePaid: boolean = true;

  emailReminderFrequencyOptions: ListItem[] = [
    new ListItem({ id: 'weekly', name: 'Weekly' }),
    new ListItem({ id: 'biweekly', name: 'Every 2 Weeks' }),
    new ListItem({ id: 'monthly', name: 'Monthly' }),
    new ListItem({ id: 'custom', name: 'Custom' }),
  ];

  paymentScheduleOptions: ListItem[] = [
    new ListItem({ id: 'Pay in Full', name: 'Pay in Full' }),
    new ListItem({ id: 'Deposit + Balance', name: 'Deposit + Balance' }),
    new ListItem({ id: 'Installments', name: 'Installments' }),
  ];
  paymentScheduleMode: string = 'Pay in Full';  

  inputTextModes = TextModes;

  loadingSpinnerRef: MatDialogRef<LoadingSpinnerComponent> | undefined;
  invoiceMerchant: any = {};

  constructor(
    public storage: StorageService,
    private router: Router,
    private access: AccessManager,
    private uiErrorHandler: UIErrorHandler,
    private invoiceService: InvoiceService,
    private uiHelpers: UIHelpers,
    private errorDialog: ErrorDialogService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private loadingSpinnerDialog: MatDialog,
  ) {
    this.access
      .getUserAccess([
        AccessManager.AUTH_CREATE_INVOICES,
        AccessManager.AUTH_VIEW_INVOICES,
      ])
      .then((access) => {
        if (!access[AccessManager.AUTH_VIEW_INVOICES]) {
          this.router.navigate(['/merchant/invoices']);
        }
      })
      .catch((message) => {
        this.uiErrorHandler.handleError(message);
      });
    this.route.queryParams.subscribe((params) => {
      this.viewInvoiceMode = params['viewInvoiceMode'] === 'true';
    });
    this.invoiceService
      .getInvoiceMerchantById(this.storage.getSelectedLocation()?.id!)
      .then((merchant) => {
        this.invoiceMerchant = merchant;
      })
      .catch((error) => {
        this.uiErrorHandler.handleError(error);
      });
  }

  ngAfterViewInit() {
    this.invoiceId = this.storage.getSelectedInvoiceId();
    this.invoiceService
      .getInvoice(this.invoiceId!)
      .then((invoice) => {
        this.invoice = invoice;
        this.merchantId = invoice.merchantId;
        this.loadInvoiceData();
      })
      .catch((error) => {
        this.uiErrorHandler.handleError(error);
      });
  }

  loadInvoiceData() {
    this.invoiceItems = this.invoice.invoiceItems;
    if (this.invoiceItems) {
      this.invoiceItems.forEach((item) => {
        item.unitPrice = this.uiHelpers.demagnifyAmount(item.unitPrice, 100);
        item.total = this.uiHelpers.demagnifyAmount(
          item.unitPrice * item.quantity,
          100,
        );
        this.lineItemsComponent?.setInvoiceItems(this.invoiceItems);
      });
    } else {
      this.invoiceItems = [];
    }
    this.tax = this.uiHelpers.demagnifyAmount(this.invoice.taxMagnified, 100);
    this.taxFormatted = UIHelpers.formatCurrency(this.tax);
    this.total = this.uiHelpers.demagnifyAmount(this.invoice.totalAmtDue, 100);
    this.totalFormatted = UIHelpers.formatCurrency(this.total);
    this.sendCustomerReceipt = this.invoice.customerReceiptEmail;
    this.sendMerchantReceipt = this.invoice.merchantReceiptEmail;
    this.sendEmailReminders = this.invoice.remindersOn;
    this.emailReminderFrequency = this.invoice.reminderFreq;
    this.customReminderFrequency = this.invoice.reminderFreqCustomDays;
    this.numberOfInstallments = this.invoice.remainingPayments;
    this.paymentInstallments = this.invoice.isInstallment;
    this.paymentInstallmentsAmount = this.total / this.numberOfInstallments;
    this.paymentInstallmentsAmountFormatted = UIHelpers.formatCurrency(
      this.paymentInstallmentsAmount,
    );
    this.invoiceNotes = this.invoice.memo;
    this.dueByDate = this.invoice.dueDate;
    if (this.invoice.customerId) {
      this.customerComponent?.loadCustomer(this.invoice.customerId);
    }
    if (this.invoice.status != 'paid') {
      this.invoicePaid = false;
    }
    this.updateTotals();
  }

  formatInvoiceItems(items: InvoiceItem[]) {
    return items.map((item) => {
      return new InvoiceItem({
        id: item.id,
        name: item.name,
        description: item.description,
        unitPrice: this.uiHelpers.magnifyAmount(item.unitPrice, 100),
        quantity: item.quantity,
        total: this.uiHelpers.magnifyAmount(item.total, 100),
      });
    });
  }

  bundleInvoiceData(status: string) {
    if (this.invoiceItems) {
      this.invoice.invoiceItems = this.formatInvoiceItems(this.invoiceItems);
    }
    this.invoice.customerId = this.selectedCustomer?.id || '';
    this.invoice.customerEmail = this.selectedCustomer?.emailAddress || '';
    this.invoice.amtDue = this.uiHelpers.magnifyAmount(this.total, 100);
    this.invoice.dueDate = this.dueByDate;
    if (this.dueByDate < new Date()) {
      this.dueByDate = new Date();
    }
    this.invoice.currencyCode = 'USD';
    this.invoice.taxMagnified = this.uiHelpers.magnifyAmount(this.tax, 100);
    this.invoice.totalAmtDue = this.uiHelpers.magnifyAmount(this.total, 100);
    this.invoice.isInstallment = this.paymentInstallments;
    if (this.numberOfInstallments > 0) {
      this.invoice.remainingPayments = Number(this.numberOfInstallments);
    } else {
      this.invoice.remainingPayments = 1;
    }
    this.invoice.sentOn = status == 'sent' ? new Date() : new Date(0);
    this.invoice.status = status;
    this.invoice.previewed = true;
    this.invoice.memo = this.invoiceNotes;
    this.invoice.reminderFreq = this.emailReminderFrequency;
    this.invoice.reminderFreqCustomDays = Number(this.customReminderFrequency);
    this.invoice.remindersOn = this.sendEmailReminders;
    this.invoice.customerReceiptEmail = this.sendCustomerReceipt;
    this.invoice.merchantReceiptEmail = this.sendMerchantReceipt;
  }

  validateInvoiceData() {
    console.log('Email Reminder Frequency', this.emailReminderFrequency);
    console.log('Custom Reminder Frequency', this.customReminderFrequency);
    if (!this.selectedCustomer) {
      this.uiErrorHandler.handleError('Please select a customer');
      return false;
    }
    if (!this.subtotal) {
      this.uiErrorHandler.handleError('Please add items to the invoice');
      return false;
    }
    if (this.sendEmailReminders && !this.emailReminderFrequency) {
      this.uiErrorHandler.handleError('Please select a reminder frequency');
      return false;
    }
    if (
      this.sendEmailReminders &&
      this.emailReminderFrequency == 'custom' &&
      !this.customReminderFrequency
    ) {
      this.uiErrorHandler.handleError(
        'Please enter a custom reminder frequency',
      );
      return false;
    }
    if (
      this.sendEmailReminders &&
      this.emailReminderFrequency == 'custom' &&
      this.customReminderFrequency < 1
    ) {
      this.uiErrorHandler.handleError(
        'Custom reminder frequency must be greater than 0',
      );
      return false;
    }
    if (this.paymentInstallments && this.numberOfInstallments < 1) {
      this.uiErrorHandler.handleError(
        'Number of installments must be greater than 0',
      );
      return false;
    }
    if (this.dueByDate && this.dueByDate < new Date()) {
      this.uiErrorHandler.handleError('Due date must be greater than today');
      return false;
    }
    return true;
  }

  handleEditInvoiceClicked() {
    this.viewInvoiceMode = !this.viewInvoiceMode;
  }

  handlePreviewAndSendClicked() {
    if (!this.validateInvoiceData()) {
      return;
    }
    let dialogRef: MatDialogRef<InvoicePreviewComponent>;
    dialogRef = this.dialog.open(InvoicePreviewComponent);
    dialogRef.componentInstance.invoice = this.invoice;
    dialogRef.componentInstance.invoice.invoiceItems = this.invoiceItems;
    dialogRef.componentInstance.invoice.totalAmtDue = this.total;
    dialogRef.componentInstance.merchantName = this.invoiceMerchant['DBA'];
    dialogRef.componentInstance.merchantEmail =
      this.invoiceMerchant['ContactEmail'];
    dialogRef.componentInstance.merchantPhone =
      this.invoiceMerchant['Phone'] || '';
    dialogRef.componentInstance.customerName =
      this.selectedCustomer?.firstName +
        ' ' +
        this.selectedCustomer?.lastName || '';
    dialogRef.componentInstance.customerEmail =
      this.selectedCustomer?.emailAddress || '';
    dialogRef.componentInstance.customerPhone =
      this.selectedCustomer?.phone || '';
    dialogRef.componentInstance.subTotal = this.subtotal;
    dialogRef.componentInstance.tax = this.tax;
    if (this.dueByDate < new Date()) {
      this.dueByDate = new Date();
    }
    let parsedDate = new Date(this.dueByDate.toString().replace(/-/g, '\/').replace(/T.+/, ''));
    dialogRef.componentInstance.dueDate = new Date(
      parsedDate,
    ).toLocaleDateString('en-us', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    dialogRef.afterClosed().subscribe((result) => {
      switch (result) {
        case 'send':
          this.loadingSpinnerRef = this.loadingSpinnerDialog.open(
            LoadingSpinnerComponent,
            {
              disableClose: true,
              panelClass: 'transparent-dialog',
              data: {
                mode: 'indeterminate',
              },
            },
          );
          this.bundleInvoiceData('sent');
          this.invoiceService
            .updateInvoice(this.invoice)
            .then(() => {
              this.loadingSpinnerRef?.close();
              this.router.navigate(['/merchant/invoices']);
            })
            .catch((error) => {
              this.uiErrorHandler.handleError(error);
              this.invoice.status = 'draft';
              this.loadingSpinnerRef?.close();
            });
          break;
        default:
          break;
      }
    });
  }

  handleSaveAsDraftClicked() {
    this.bundleInvoiceData('draft');
    this.invoice.saveAsDraft = true;
    this.invoiceService
      .updateInvoice(this.invoice)
      .then(() => {
        this.router.navigate(['/merchant/invoices']);
      })
      .catch((error) => {
        this.snackBar.openFromComponent(CustomSnackbarComponent, {
          duration: 5000,
          panelClass: 'snackbar-error',
          data: {
            message: 'Failed to save invoice.',
            fontSize: '1.5em',
            iconSize: '50px',
          },
        });
      });
    return;
  }

  handleSelectedCustomerChanged(customer: Partial<Customer>) {
    this.selectedCustomer = customer;
  }

  handleDiscardClicked() {
    this.invoiceService.deleteInvoice(this.invoice.id).then(() => {
      this.router.navigate(['/merchant/invoices']);
    }).catch((error) => {
      this.uiErrorHandler.handleError(error);
    });
  }

  handleManageInvoicesClicked() {
    this.router.navigate(['/merchant/invoices']);
  }

  handleInvoiceSettingsClicked() {
    this.router.navigate(['/merchant/invoices/settings']);
  }

  handleSubtotalChange(subtotal: number) {
    this.subtotal = subtotal;
    this.updateTotals();
  }

  handleTaxChange() {
    this.taxFormatted = UIHelpers.formatCurrency(this.tax);
    this.updateTotals();
  }

  handleNumberOfInstallmentsChange() {
    this.paymentInstallmentsAmount = this.subtotal / this.numberOfInstallments;
    this.updateTotals();
  }

  handleInstallmentsAmountChange() {
    this.paymentInstallmentsAmountFormatted = UIHelpers.formatCurrency(
      this.paymentInstallmentsAmount,
    );
  }

  validateDate(date: string): boolean {
    let tempDate = new Date(date);
    tempDate = new Date(
      tempDate.getTime() - tempDate.getTimezoneOffset() * -60000,
    );
    if (tempDate.toString() === 'Invalid Date') {
      return false;
    }
    if (isNaN(tempDate.getTime())) {
      return false;
    }
    if (tempDate.getFullYear().toString().length != 4) {
      return false;
    }
    return true;
  }

  handleDueByDateChange(date: string) {
    if (this.validateDate(date)) {
      const currentDate = new Date();
      const dueDate = new Date(date);
      this.dueByDate = new Date(
        dueDate.getTime() - dueDate.getTimezoneOffset() * -60000,
      );
      this.dueByDate.setHours(
        currentDate.getHours(),
        currentDate.getMinutes(),
        currentDate.getSeconds(),
        currentDate.getMilliseconds(),
      );
    }
  }

  handlePauseRemindersUntilChange(date: string) {
    if (this.validateDate(date)) {
      this.pauseRemindersUntil = new Date(date);
      this.pauseRemindersUntil = new Date(
        this.pauseRemindersUntil.getTime() -
          this.pauseRemindersUntil.getTimezoneOffset() * -60000,
      );
      this.pauseRemindersEnabled = true;
    }
  }

  handlePauseRemindersClicked() {
    this.invoiceService
      .pauseReminders(this.invoice.id, this.pauseRemindersUntil)
      .then(() => {
        this.snackBar.openFromComponent(CustomSnackbarComponent, {
          duration: 5000,
          panelClass: 'snackbar-success',
          data: {
            message: 'Reminders paused successfully.',
            fontSize: '1.5em',
            iconSize: '50px',
          },
        });
      })
      .catch((error) => {
        this.snackBar.openFromComponent(CustomSnackbarComponent, {
          duration: 5000,
          panelClass: 'snackbar-error',
          data: {
            message: 'Failed to pause reminders.',
            fontSize: '1.5em',
            iconSize: '50px',
          },
        });
      });
  }

  handlePaymentInstallmentsChange() {
    if (this.paymentInstallments) {
      this.numberOfInstallments = 1;
    } else {
      this.numberOfInstallments = 0;
    }
    this.handleNumberOfInstallmentsChange();
  }

  handleSendReminderClicked() {
    this.invoiceService
      .sendReminder(
        this.invoice.id,
        this.merchantId!,
        this.selectedCustomer?.id!,
        this.selectedCustomer?.emailAddress!,
      )
      .then(() => {
        this.snackBar.openFromComponent(CustomSnackbarComponent, {
          duration: 5000,
          panelClass: 'snackbar-success',
          data: {
            message: 'Invoice reminder sent successfully.',
            fontSize: '1.5em',
            iconSize: '50px',
          },
        });
      })
      .catch((error) => {
        this.snackBar.openFromComponent(CustomSnackbarComponent, {
          duration: 5000,
          panelClass: 'snackbar-error',
          data: {
            message: 'Failed to send invoice reminder.',
            fontSize: '1.5em',
            iconSize: '50px',
          },
        });
      });
  }

  updateTotals() {
    this.total =
      Number(this.subtotal) +
      Number(this.taxFormatted.replace('$', '').replaceAll(',', ''));
    this.totalFormatted = UIHelpers.formatCurrency(this.total);
    if (this.numberOfInstallments > 0) {
      this.paymentInstallmentsAmount = this.total / this.numberOfInstallments;
    } else {
      this.paymentInstallmentsAmount = 0;
    }
  }
}
